import React, { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { useTranslation } from 'react-i18next';

import 'mapbox-gl/dist/mapbox-gl.css';
import styles from '../styles.module.scss';

mapboxgl.accessToken = import.meta.env.VITE_MAP_API_TOKEN;

const MAP_STYLE = import.meta.env.VITE_MAPBOX_STYLE || 'mapbox://styles/mapbox/streets-v12';
const MAPBOX_ZOOM = 9;

const MapBoxContainer = ({ children, currentPosition }) => {
  const { t } = useTranslation();
  const mapContainer = useRef(null);
  const mapWrapperRef = useRef(null);
  const map = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      mapWrapperRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => map.current?.resize(), 100);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAP_STYLE,
      center: [currentPosition.ln, currentPosition.lat],
      zoom: MAPBOX_ZOOM,
      attributionControl: false,
      logoPosition: 'bottom-right',
    });
    map.current.addControl(new MapboxLanguage({ defaultLanguage: 'ru' }));
  });

  useEffect(() => {
    return () => map.current.remove();
  }, []);

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { map, mapboxgl });
    }
    return child;
  });

  return (
    <div ref={mapWrapperRef} className={`${styles.mapWrapper} ${isFullscreen ? styles.fullscreen : ''}`}>
      <button
        type="button"
        className={styles.fullscreenButton}
        onClick={toggleFullscreen}
        title={isFullscreen ? t('map.exitFullscreen') : t('map.fullscreen')}
      >
        {isFullscreen ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        )}
      </button>
      <div className={styles.mapContainer}>
        <div ref={mapContainer} className={styles.map} />
        {childrenWithProps}
      </div>
    </div>
  );
};

export default MapBoxContainer;
